import * as React from 'react';
import { IndexRoute, Route } from 'react-router';

import $ from 'jquery';
import LucidJS from 'lucidjs';
import _ from 'underscore';

import path from 'js/lib/path';

import { createMountNode, runTrackingRouter } from 'bundles/page/lib/reactClientApps';

let reactRouterAdapter;
const App = ({ children }) => {
  return (
    <div>
      <div data-js="origami" />
      {children}
    </div>
  );
};
App.propTypes = {
  children: React.PropTypes.node,
};

class NotFoundRedirect extends React.Component {
  componentWillMount() {
    reactRouterAdapter.trigger('error', 404);
  }

  render() {
    return null;
  }
}

const legacyRouteHandler = (route, getLegacyRouteParams, callback) => {
  return class LegacyRouteHandler extends React.Component {
    static propTypes = {
      location: React.PropTypes.object.isRequired,
      route: React.PropTypes.object.isRequired,
    };

    static contextTypes = {
      router: React.PropTypes.object.isRequired,
    };

    state = {
      userConfirmed: false,
    };

    componentDidMount() {
      this.executeLegacyRoute();
    }

    componentDidUpdate(previousProps) {
      // `location.key` is guaranteed to change when the location changes, so it's safe to only execute a route when
      // the key changes. See: https://github.com/reactjs/react-router/blob/master/docs/Glossary.md#locationkey
      if (this.props.location.key !== previousProps.location.key) {
        this.executeLegacyRoute();
      }
    }

    executeLegacyRoute() {
      const legacyParams = getLegacyRouteParams(route, this.context.router.params);
      callback.apply(this, legacyParams.concat(this.context.router.location.query));

      this.context.router.setRouteLeaveHook(this.props.route, this.routerWillLeave);
    }

    routerWillLeave = (nextLocation) => {
      if (this.state.userConfirmed) {
        return true;
      } else if (nextLocation) {
        this.setState({
          nextLocation,
        });

        reactRouterAdapter.trigger('routerWillLeave', (userConfirmed) => {
          if (userConfirmed) {
            this.setState(
              {
                userConfirmed,
              },
              () => {
                this.context.router.push(this.state.nextLocation);
              }
            );
          } else {
            this.setState({
              nextLocation: null,
              userConfirmed: false,
            });
          }
        });

        return false;
      }
    };

    render() {
      return <div />;
    }
  };
};

class ReactRouterAdapter {
  routes = {};

  nativeRoutes = [];

  visitedFragments = [];

  constructor() {
    LucidJS.emitter(this);
  }

  addRoutes(routes, routeCallbackWrapper) {
    if (routeCallbackWrapper) {
      Object.assign(this.routes, _(routes).mapObject(routeCallbackWrapper));
    } else {
      Object.assign(this.routes, routes);
    }
  }

  addNativeRoutes(routes = []) {
    this.nativeRoutes.push(routes);
  }

  getLegacyRoutes(rootPath) {
    return _(this.routes).map((callback, route) => {
      if (!route) {
        return (
          <IndexRoute
            key={`index:${rootPath}`}
            component={legacyRouteHandler(route, this.getLegacyRouteParams, callback)}
          />
        );
      } else {
        return (
          <Route key={route} path={route} component={legacyRouteHandler(route, this.getLegacyRouteParams, callback)} />
        );
      }
    });
  }

  getRoutes(rootPath) {
    if (!this.getLegacyRoutes(rootPath).length && this.nativeRoutes.length) {
      // If we only have native routes, let's assume that those native routes will handle everything.
      return this.nativeRoutes;
    } else {
      const routes = [
        ...this.getLegacyRoutes(rootPath),
        ...this.nativeRoutes,
        <Route path="*" key="notFoundRoute" component={NotFoundRedirect} />,
      ];

      return (
        <Route path={rootPath} component={App}>
          {routes}
        </Route>
      );
    }
  }

  // The `params` object from react-router is an object, and thus no order is enforced.
  // However, backbone route handlers are invoked with all parameters, in-order, as arguments.
  // Thus, we must parse for them and order the parameters provided by react-router.
  getLegacyRouteParams(route, params) {
    const paramRegex = /\:\w*/g;
    const matches = route.match(paramRegex);
    if (!matches) {
      return [];
    } else {
      return _(matches)
        .chain()
        .compact()
        .invoke('slice', 1)
        .reduce(function (memo, match) {
          return memo.concat(params[match]);
        }, [])
        .value();
    }
  }

  setupLinks(routePrefix, linkSelector) {
    this.routePrefix = routePrefix;
    this.linkSelector = linkSelector;
    $('body').on(
      'click',
      'a' + this.linkSelector,
      function (e) {
        if (!e.ctrlKey && !e.metaKey) {
          e.preventDefault();
          this.navigate($(e.currentTarget).attr('href'), true);
        }
      }.bind(this)
    );
  }

  navigate(_fragment, options) {
    let fragment = _fragment;
    // You don't actually want to use this
    // Use ReactRouter instead
    if (fragment.indexOf(this.routePrefix) !== 0) {
      fragment = path.join(this.routePrefix, fragment);
    }

    if (!options || !options.replace) {
      this.addFragmentToVisited(fragment);
    }

    // RR always calls the route handler on change,
    // while Backbone Router only did if trigger === true
    if (options === true || (options && options.trigger === true)) {
      if (options && options.replace) {
        this.rr.replace(fragment);
      } else {
        this.rr.push(fragment);
      }
    } else if (options && options.replace) {
      window.history.replaceState(null, null, fragment);
    } else {
      window.history.pushState(null, null, fragment);
    }
  }

  navigateTo(href, options) {
    // You don't actually want to use this
    // Use ReactRouter instead
    if (!href && href !== '') {
      throw new Error('router.navigateTo received no href');
    }
    this.navigate(href.replace(this.routePrefix, ''), options);
  }

  addFragmentToVisited(fragment) {
    this.visitedFragments.push(fragment);
  }

  start(rootPath, nativeRoutes) {
    const mountNode = createMountNode('rendered-content');
    runTrackingRouter(this.getRoutes(rootPath), mountNode).then((router) => {
      this.rr = router;
    });
  }
}

reactRouterAdapter = new ReactRouterAdapter();

export default reactRouterAdapter;
